/**
 * useTodoNavigate — 할일 줄·처리 버튼 동작. 서버가 내려준 값 그대로 처리한다(화면이 행선지를 고르지 않음).
 *  - 줄 클릭: todo.rowHref(viewPath)로 이동
 *  - 처리 버튼: 동작 유형 ROUTE 면 todo.href(webPath)로 이동, API 면 todo.actionUrl 을 호출한 뒤 할일을 다시 조회
 *    API 호출은 성공 여부(true/false)를 Promise 로 돌려준다 — 줄(TodoRow)이 버튼을 처리 중 · 완료 표시로 바꾸는 데 쓴다.
 * 슬라이드(드로어) 안에서 쓸 때는 beforeNavigate 로 드로어를 닫고 이동한다.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, useAuthStore } from '@indipia/shared';
import { toast } from 'sonner';
import type { TodoItem } from './todoModel';

/** row = 줄 클릭 · action = 처리 버튼 */
export type TodoNavTarget = 'row' | 'action';

export function useTodoNavigate(beforeNavigate?: () => void) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useCallback((todo: TodoItem, target: TodoNavTarget = 'action'): Promise<boolean> | void => {
    if (target === 'action' && todo.actionType === 'API') {
      if (!todo.actionUrl) return;
      return apiClient.post(todo.actionUrl, null, {
        headers: {
          ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
          ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
        },
      })
        .then(() => {
          qc.invalidateQueries({ queryKey: ['todos'] });
          return true;
        })
        .catch(() => {
          toast.error('처리하지 못했어요. 잠시 후 다시 시도해 주세요.');
          return false;
        });
    }
    const to = target === 'row' ? todo.rowHref : todo.href;
    if (!to) return;
    beforeNavigate?.();
    navigate(to);
  }, [navigate, beforeNavigate, qc, user?.compId, user?.memberId]);
}
